import { alpha } from '@mui/material/styles';
import theme from '../../theme/theme';
import type { BadgeColors } from './Badge';

/**
 * Semantic badge variants — the single source of truth for the colour recipe that
 * was previously hand-rolled at every call site
 * (`{ color: X.dark, bgcolor: alpha(X.main, .1), dot: X.dark }`).
 *
 * Feature status maps should stay palette-free and map their domain status to one
 * of these variant names (e.g. `closed → 'success'`), rather than re-deriving
 * colours. For one-off colours, pass explicit props to {@link Badge} directly.
 */
export type BadgeVariant =
  | 'primary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'neutral'
  | 'blank';

/** The one recipe: dark text/dot, 10%-alpha main as background. */
const recipe = (main: string, dark: string): BadgeColors => ({
  color: dark,
  bgcolor: alpha(main, 0.1),
  dot: dark,
});

export const BADGE_VARIANTS: Record<BadgeVariant, BadgeColors> = {
  primary: recipe(theme.palette.primary.main, theme.palette.primary.dark),
  success: recipe(theme.palette.success.main, theme.palette.success.dark),
  warning: recipe(theme.palette.warning.main, theme.palette.warning.dark),
  error: recipe(theme.palette.error.main, theme.palette.error.dark),
  info: recipe(theme.palette.info.main, theme.palette.info.dark),
  // Neutral is based on the legible label grey (text.label) so it works both as a
  // muted pill and as full-line row text (single origin — see config/payslipPresentation).
  neutral: recipe(theme.palette.text.label, theme.palette.text.label),
  // Blank = white / no tint (spec "blanc"): used by the calculation rubrique, which
  // must read as un-highlighted. Text stays a legible body colour (a white amount
  // would be invisible); only the background/marker are white — the marker gets a
  // border at its call site so it stays visible on a white surface.
  blank: {
    color: theme.palette.text.body,
    bgcolor: theme.palette.common.white,
    dot: theme.palette.common.white,
  },
};
